<template>
  <section id="adverpeople">
    <div class="wrap">
      <div class="head">
        <h2 class="h2">See who's watching</h2>
        <p class="lede">Real impressions, attention time and audience demographics for every campaign on enabled screens. Proof your advertisers can see.</p>
      </div>
      <div class="metrics mt">
        <div class="metric"><small>Impressions</small><b>12,450</b></div>
        <div class="metric"><small>Dwell time</small><b>6.8s</b></div>
        <div class="metric"><small>Demographics</small><b>25–44</b></div>
        <div class="metric"><small>Attribution</small><b>Per screen</b></div>
      </div>
      <div class="apdash">
        <!-- Markup and styling follow the published page's "By hour / By display"
             card. -->
        <div
          class="card apeng"
          :class="{ 'v-display': view === 'display' }"
        >
          <h4>
            <span class="vtabs" role="tablist" aria-label="Engagement view" @keydown="onTabKey">
              <button
                v-for="(v, i) in views"
                :id="'apv-tab-' + v.key"
                :key="v.key"
                ref="tabs"
                type="button"
                role="tab"
                :aria-selected="String(view === v.key)"
                :aria-controls="'apv-' + v.key"
                :tabindex="view === v.key ? 0 : -1"
                @click="select(i)"
              >{{ v.label }}</button>
            </span>
            <span class="seg" role="group" aria-label="Chart measure">
              <button type="button" :aria-pressed="String(mode === 'rate')" @click="mode = 'rate'">Rate</button>
              <button type="button" :aria-pressed="String(mode === 'count')" @click="mode = 'count'">Count</button>
            </span>
          </h4>

          <!-- Both panels share the card's second grid row (.apv in
               _artifact.scss), so switching never changes the card's size;
               the inactive one is .off (hidden, inert to the pointer). -->
          <div
            id="apv-hour"
            class="apv"
            :class="{ off: view !== 'hour' }"
            data-v="hour"
            role="tabpanel"
            aria-labelledby="apv-tab-hour"
            :aria-hidden="String(view !== 'hour')"
          >
            <div class="apv-t">Active engagement by hour</div>
            <svg
              id="apChart"
              ref="chart"
              class="chart"
              viewBox="0 0 560 230"
              role="img"
              aria-label="Engagement by hour, 7am to 9pm"
            ></svg>
            <div class="apv-sum"><div><small>Peak hour</small><b>5pm</b></div><div><small>Engaged today</small><b>1,656</b></div><div><small>Busiest stretch</small><b>4 to 6pm</b></div></div>
          </div>

          <div
            id="apv-display"
            ref="display"
            class="apv dsp"
            :class="{ off: view !== 'display', 'pre-chart': preChart }"
            data-v="display"
            role="tabpanel"
            aria-labelledby="apv-tab-display"
            :aria-hidden="String(view !== 'display')"
          >
            <div class="dsp-hd">
            <div><div class="dsp-name">Harbour-Grocer-Bayside-12 <span class="dsp-on">Online</span></div><small>Checkouts</small></div>
            </div>
            <div class="dsp-kpis">
            <div><small>Foot traffic</small><b data-n>1,184</b></div>
            <div><small>Total dwell</small><b>63h 40m</b></div>
            <div><small>Active engagement</small><b data-n>296</b></div>
            <div><small>Engagement rate</small><b>25%</b></div>
            </div>
            <div class="dsp-charts">
            <div>
            <small>Gender</small>
            <div class="dsp-donut-w">
            <svg class="dsp-donut" viewBox="0 0 120 120" aria-label="Gender split: 44% male, 56% female" role="img">
            <circle cx="60" cy="60" r="46" fill="none" stroke="var(--dsp-b)" stroke-width="16"/>
            <circle class="dsp-arc" cx="60" cy="60" r="46" fill="none" stroke="var(--dsp-a)" stroke-width="16" stroke-dasharray="161.9 300" transform="rotate(-90 60 60)" stroke-linecap="butt"/>
            <text x="60" y="58" text-anchor="middle">1,184</text><text x="60" y="74" text-anchor="middle" class="sm">people</text>
            </svg>
            <ul class="dsp-key"><li><i style="background:var(--dsp-b)"></i>Male <b>44%</b></li><li><i style="background:var(--dsp-a)"></i>Female <b>56%</b></li></ul>
            </div>
            </div>
            <div>
            <small>Age distribution</small>
            <div class="dsp-bars" role="img" aria-label="Age distribution">
            <div><span style="--h:28%"></span><em>18–24</em></div>
            <div><span style="--h:72%"></span><em>25–34</em></div>
            <div><span style="--h:100%"></span><em>35–44</em></div>
            <div><span style="--h:81%"></span><em>45–54</em></div>
            <div><span style="--h:52%"></span><em>55+</em></div>
            </div>
            </div>
            </div>
            <div class="dsp-ft">24 campaigns on this display</div>
        
          </div>
        </div>

        <div class="card">
          <h4>Campaigns <span class="sample">Sample data</span></h4>
          <div style="overflow-x:auto">
            <table class="ctab">
              <thead><tr><th>Campaign</th><th>Impr.</th><th>Engaged</th><th>Eng. %</th></tr></thead>
              <tbody>
                <tr><td>Weekly specials</td><td>5,204</td><td>1,978</td><td class="pc">38%</td></tr>
                <tr><td>Bakery fresh</td><td>3,118</td><td>967</td><td class="pc">31%</td></tr>
                <tr><td>Local wines</td><td>2,640</td><td>713</td><td class="pc">27%</td></tr>
                <tr><td>Pharmacy</td><td>1,488</td><td>327</td><td class="pc">22%</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Extracted from the published landing page; in-page anchors emit instead of
// navigating, because the app uses a hash router.
import { drawEngagementChart, isNarrowChart } from "./widgets";

export default {
  name: "AdverpeopleSection",
  data() {
    return {
      mode: "count",
      views: [
        { key: "hour", label: "By hour" },
        { key: "display", label: "By display" },
      ],
      view: "hour",
      // Set for one frame when the display view opens, so its donut and bars
      // grow in (the .pre-chart rules in _display-view.scss).
      preChart: false,
      reduceMotion: false,
    };
  },
  mounted() {
    this.draw();
    // Redraw only when crossing the narrow breakpoint, not on every resize.
    this.narrow = isNarrowChart();
    this.onResize = () => {
      const n = isNarrowChart();
      if (n !== this.narrow) {
        this.narrow = n;
        this.draw();
      }
    };
    window.addEventListener("resize", this.onResize);
    this.reduceMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.growTimer);
  },
  watch: {
    mode() {
      this.draw();
    },
  },
  methods: {
    draw() {
      drawEngagementChart(this.$refs.chart, this.mode);
    },

    // Same sequence as the published page: start collapsed, then release so
    // the transitions run.
    grow() {
      this.preChart = true;
      this.$nextTick(() => {
        // Force a layout so the collapsed state is painted before release.
        void (this.$refs.display && this.$refs.display.offsetWidth);
        clearTimeout(this.growTimer);
        this.growTimer = setTimeout(() => {
          this.preChart = false;
        }, 120);
      });
    },
    current() {
      return this.views.findIndex((v) => v.key === this.view);
    },
    select(i) {
      const key = this.views[i].key;
      if (key === this.view) return;
      this.view = key;
      if (key === "display" && !this.reduceMotion) this.grow();
    },
    // Arrow keys move between tabs, as the ARIA tabs pattern expects.
    onTabKey(e) {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      if (!step) return;
      e.preventDefault();
      const n = this.views.length;
      this.select((this.current() + step + n) % n);
      this.$nextTick(() => {
        const tab = this.$refs.tabs && this.$refs.tabs[this.current()];
        if (tab) tab.focus();
      });
    },
  },
};
</script>
